import { OptionsCategoryContent } from "./OptionsCategoryContent";
import { Showable } from "../../../mixins/Showable";
import { ContentsScrollPane } from "../ContentsScrollPane";
import { OptionsCategoryCompoundData, OptionsCategoryParsedLocData, OptionsCategoryParsedThemeData, OptionsCategoryLayoutData, OptionsLayoutData, TextStatesData, TextureStatesData } from "../../../types/types";
import { LayoutDataManager } from "../../managers/LayoutDataManager";
import { AppLocalization, LanguageId } from "../../localization/AppLocalization";
import { ThemeDataManager } from "../../managers/ThemeDataManager";
import { VariationId } from "../TableScreen";
import { LoaderFileKeys } from "../../constant/AppConstants";
import { AppSettings } from "../../managers/AppSettings";
import { OptionsItemBase } from "./OptionsItemBase";
import { OptionsItemType } from "./OptionsItem";


// get ts linting
export interface OptionsContentScroll extends Showable { }

/**
 * multiple category contents stacked on top of eachother in containers.
 * (EXTENDS) scrollpane for the above (overlay, mask, above contents in another container)
 * sets data (knows all datamodels and howto redistribute) for the above and 
 * can sum GetSize on all containers as this.content.GetSize
 */
export class OptionsContentScroll
    extends ContentsScrollPane {
    protected contentsData: OptionsCategoryCompoundData[];
    // following 2 vars reflect current component state
    public curVariationId: VariationId;
    public curLanguageId: LanguageId;

    // access for categories
    public catItemsData: {
        bgTextureStates?: TextureStatesData;
        bgTextureToggleStates?: TextureStatesData;
        labelTextStates?: TextStatesData;
        labelTextToggleStates?: TextStatesData;
        selectorLabelTextStates?: TextStatesData;
        valueTextStates?: TextStatesData;
        poolGroupKeysByItemType?: string[];
    }

    public override Init(
        cullItems: boolean = true,
        referenceWidth: number = undefined,
        variationId: VariationId = undefined,
        languageId: LanguageId = undefined,
    ): void {
        // > referenceWidth
        // > scrollAxis
        // > excessScrollAmount
        // > contentsScrollData
        // > contents
        super.Init(cullItems, referenceWidth);

        this.catItemsData = {};
        this.catItemsData.bgTextureStates = {
            // out: {texture:LoaderFileKeys.ui, frame:"settings-listItem-bg.png"},
            out: { texture: LoaderFileKeys.ui, frame: "rect-2x2-transparent.png" },
        };
        this.catItemsData.bgTextureToggleStates = {
            // out: {texture:LoaderFileKeys.ui, frame:"settings-listItem-bg.png"},
            out: { texture: LoaderFileKeys.ui, frame: "rect-2x2-transparent.png" },
        };
        this.catItemsData.labelTextStates = {
            out: {
                color: "#fff",
                fontSize: "40px",
                align: "left",
                fontFamily: AppSettings.fontFamilies.MontserratBold,
                metrics: AppSettings.metrics.montserratBold40,
            },
            over: {
                color: "#ffb82d",
                fontSize: "40px",
                align: "left",
                fontFamily: AppSettings.fontFamilies.MontserratBold,
                metrics: AppSettings.metrics.montserratBold40,
            },
        };
        this.catItemsData.labelTextToggleStates = {
            out: {
                color: "#fff",
                fontSize: "40px",
                align: "left",
                fontFamily: AppSettings.fontFamilies.MontserratBold,
                metrics: AppSettings.metrics.montserratBold40,
            },
            over: {
                color: "#ffb82d",
                fontSize: "40px",
                align: "left",
                fontFamily: AppSettings.fontFamilies.MontserratBold,
                metrics: AppSettings.metrics.montserratBold40,
            },
        }
        this.catItemsData.selectorLabelTextStates = {
            out: {
                color: "#fff",
                fontSize: "40px",
                align: "left",
                fontFamily: AppSettings.fontFamilies.MontserratBold,
                metrics: AppSettings.metrics.montserratBold40,
            },
            over: {
                color: "#ffb82d",
                fontSize: "40px",
                align: "left",
                fontFamily: AppSettings.fontFamilies.MontserratBold,
                metrics: AppSettings.metrics.montserratBold40,
            },
        };
        this.catItemsData.valueTextStates = {
            out: {
                color: "#fff",
                fontSize: "40px",
                align: "left",
                fontFamily: AppSettings.fontFamilies.MontserratBold,
                metrics: AppSettings.metrics.montserratBold40,
            },
            over: {
                color: "#ffb82d",
                fontSize: "40px",
                align: "left",
                fontFamily: AppSettings.fontFamilies.MontserratBold,
                metrics: AppSettings.metrics.montserratBold40,
            },
        };
        this.catItemsData.poolGroupKeysByItemType = [];
        let itemTypesCount: number = Object.keys(OptionsItemType).length / 2; // div2 because enum stores both keys and values separately
        for (let i = 0; i < itemTypesCount; i++) {
            this.catItemsData.poolGroupKeysByItemType[i] = "OptionsItem" + i;
        }

        this.excessScrollAmount.Set(0, 0, 0, 50);

        this.curVariationId = variationId;
        this.curLanguageId = languageId;

        // update data model for Build() categories
        this.UpdateContentsData();
    }

    public Build(): void {
        // >> build overlay
        // > build content container
        // > build mask
        super.Build();

        this.contentsData.forEach((contentData, i) => {
            let catContent = new OptionsCategoryContent(this.scene);
            // catContent.name = "OptionsCategoryContent_"+i;
            catContent.Init(this, contentData);
            catContent.Build();
            this.content.add(catContent);
            this.contents[i] = catContent;
        });
    }

    public SetData(
        variationId: VariationId = undefined,
        languageId: LanguageId = undefined,
    ): void {
        // console.log("SetData");

        if (variationId != undefined) {
            this.curVariationId = variationId;
        }
        if (languageId != undefined) {
            this.curLanguageId = languageId;
        }

        // parse new models into usable runtime model
        this.UpdateContentsData();

        // NOTE: number of cats STAYS. Number/type of items within each cat CHANGES.
        this.contents.forEach((
            catContent: OptionsCategoryContent,
            catIndex: number,
        ) => {
            // console.log(catIndex);
            catContent.SetData(this.contentsData[catIndex]);
        });

        // update all contents sizes and items and btnHelp positions etc.
        if (this.size) {
            this.SetSize(this.size.width, this.size.height);
        }
    }

    public GetItemById(itemId: string): OptionsItemBase | null {
        for (let i = 0; i < this.contents.length; i++) {
            const catContent: OptionsCategoryContent = this.contents[i] as OptionsCategoryContent;
            const item: OptionsItemBase = catContent.GetItemById(itemId);
            if (item != null) {
                return item;
            }
        }
        return null;
    }

    private UpdateContentsData(): void {
        // console.log("UpdateContentsData", LanguageId[this.curLanguageId]);

        // fetch data
        this.contentsData = [];
        // const optionsSharedLiveData: OptionsLiveData = OptionsDataManager.GetSharedData();
        const optionsVariationLayoutData: OptionsLayoutData = LayoutDataManager.GetVariationData(this.curVariationId).options;
        const optionsSharedLocData = AppLocalization.GetSharedData(this.curLanguageId).options;
        const optionsSharedThemeData = ThemeDataManager.GetSharedData().options;
        optionsVariationLayoutData.categories.forEach((categoryLayoutData: OptionsCategoryLayoutData, categoryIndex: number) => {
            // parse theme data
            const catThemeData: OptionsCategoryParsedThemeData = {
                title: optionsSharedThemeData.categoryItems[categoryIndex],
                itemsById: optionsSharedThemeData.itemsById
            };
            // parse loc data
            const catLocData: OptionsCategoryParsedLocData = {
                title: optionsSharedLocData.catTitles[categoryIndex],
                // just pass whole shared loc data model 
                // (but pick by contentData.layout what you need inside OptionsCategoryContent)
                itemsById: optionsSharedLocData.itemsById
            };
            // parse live data
            // EDIT: live data now split into shared and variation; get specific items as you need them through layout ids
            // const catLiveData: OptionsCategoryParsedLiveData = {
            //     itemsById: optionsSharedLiveData.itemsById
            // };
            const contentData: OptionsCategoryCompoundData = {
                layout: optionsVariationLayoutData.categories[categoryIndex],
                theme: catThemeData,
                loc: catLocData,
                // live: catLiveData,
            };
            this.contentsData.push(contentData);
        });
    }

    public override UpdateFromLocalizationChange(): void {
        // console.trace("UpdateFromLocalizationChange");

        // update this.contentsData:OptionsCategoryCompoundData from new data models
        // this.UpdateContentsData(); // this already happened

        // NOTE: Options uses variation-specific loc (TODO:brainstorm again)
        this.contentsData.forEach((categoryCompoundData: OptionsCategoryCompoundData, categoryIndex: number) => {
            // this level's contentsScrollData for parent
            const title: string = categoryCompoundData.loc.title;
            this.contentsScrollData[categoryIndex] = {
                title: title,
                capitalizedTitle: title.toUpperCase(),
            }
            // // content view
            // const catContent: OptionsCategoryContent = this.contents[categoryIndex] as OptionsCategoryContent;
            // catContent.SetData(categoryCompoundData);
        });
    }
}